import React from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { formatNoteTime } from './drawerFormat.js';
import { NOTE_TEMPLATES, noteQuality } from './noteTemplates.js';
import {
  auditActionPreview,
  parseAuditLinkId,
  stripAuditLinkToken,
  withAuditLinkToken,
} from './investigationFeedHelpers.js';
import { useInvestigationAuditLink } from './useInvestigationAuditLink.js';
import { InvestigationTemplatePicker } from './InvestigationTemplatePicker.jsx';

const noteAvatarColors = (author) => {
  const palette = [
    ['#1976d2', '#42a5f5'],
    ['#6a1b9a', '#ab47bc'],
    ['#2e7d32', '#66bb6a'],
    ['#ef6c00', '#ffa726'],
    ['#c62828', '#ef5350'],
    ['#00838f', '#26c6da'],
  ];
  let hash = 0;
  for (let index = 0; index < String(author || '?').length; index += 1) {
    hash = ((hash << 5) - hash) + String(author || '?').charCodeAt(index);
  }
  return palette[Math.abs(hash) % palette.length];
};

const initials = (author) => (author || '?')
  .split(/\s+/)
  .map((part) => part[0])
  .filter(Boolean)
  .slice(0, 2)
  .join('')
  .toUpperCase();

export const CategorizePanel = ({
  categories,
  category,
  remediations,
  remediation,
  setCategory,
  setRemediation,
}) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Categorize & remediate</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Autosaved</Typography>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Autocomplete
          freeSolo
          fullWidth
          options={categories}
          value={category}
          onChange={(_event, value) => setCategory(value || null)}
          onInputChange={(_event, value) => setCategory(value || null)}
          renderInput={(params) => (
            <TextField {...params} label="Variance category" size="small" placeholder="Select or type new..." />
          )}
        />
        <Autocomplete
          freeSolo
          fullWidth
          options={remediations}
          value={remediation}
          onChange={(_event, value) => setRemediation(value || null)}
          onInputChange={(_event, value) => setRemediation(value || null)}
          renderInput={(params) => (
            <TextField {...params} label="Remediation step taken" size="small" placeholder="Select or type new..." />
          )}
        />
      </Stack>
    </Stack>
  </Paper>
);

const AuditLinkChip = ({ action, onDelete = null, onClick = null }) => {
  const preview = auditActionPreview(action || {});
  return (
    <Chip
      size="small"
      clickable={Boolean(onClick)}
      onClick={onClick}
      onDelete={onDelete || undefined}
      icon={<LinkOutlinedIcon />}
      label={preview.title || 'Audit link'}
      variant="outlined"
      color="info"
      sx={{ maxWidth: 260, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
    />
  );
};

const AuditPreviewPopover = ({ anchorEl, action, onClose }) => {
  const preview = auditActionPreview(action || {});
  return (
    <Popover
      open={Boolean(anchorEl && action)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { p: 1.25, width: 320, maxWidth: 'calc(100vw - 32px)' } } }}
    >
      <Stack spacing={0.75}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{preview.title}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{preview.route}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>{preview.product}</Typography>
        {(preview.price || preview.proposedPrice) && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {[preview.price && `Price ${preview.price}`, preview.proposedPrice && `Proposed ${preview.proposedPrice}`].filter(Boolean).join(' - ')}
          </Typography>
        )}
        {preview.problem && <Typography variant="caption">Problem: {preview.problem}</Typography>}
        {preview.fix && <Typography variant="caption">Fix: {preview.fix}</Typography>}
        {preview.note && <Typography variant="caption">Note: {preview.note}</Typography>}
      </Stack>
    </Popover>
  );
};

const NoteText = ({ text, actionById }) => {
  const actionId = parseAuditLinkId(text);
  const body = stripAuditLinkToken(text);
  const action = actionId ? actionById.get(actionId) : null;
  const [anchorEl, setAnchorEl] = React.useState(null);
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
      {body ? (
        <Typography
          component="span"
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}
        >
          {body}
        </Typography>
      ) : null}
      {actionId ? (
        <AuditLinkChip action={action || { actionLabel: 'Audit link' }} onClick={(event) => setAnchorEl(event.currentTarget)} />
      ) : null}
      <AuditPreviewPopover anchorEl={anchorEl} action={action || { actionLabel: 'Audit link', id: actionId }} onClose={() => setAnchorEl(null)} />
    </Stack>
  );
};

const NoteCard = ({ note, onEdit, onDelete, actionById, activeDraftNoteId = null }) => {
  const isSystem = note.status === 'system' || note.author === 'System';
  const isDraft = note.status === 'draft' && note.id && note.id === activeDraftNoteId;
  const [colorA, colorB] = noteAvatarColors(note.author);
  return (
    <Box
      sx={{
        p: 1.5,
        pl: 1.75,
        borderLeft: '3px solid',
        borderColor: isDraft ? 'warning.main' : isSystem ? 'divider' : 'primary.light',
        bgcolor: isDraft
          ? (theme) => (theme.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.08)' : 'rgba(237, 108, 2, 0.04)')
          : isSystem
            ? 'background.default'
            : 'action.hover',
        borderRadius: '0 6px 6px 0',
        opacity: isSystem ? 0.85 : 1,
        transition: 'background-color 120ms',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: isSystem ? 'linear-gradient(135deg, rgba(0,0,0,0.06), rgba(0,0,0,0.12))' : `linear-gradient(135deg, ${colorA}, ${colorB})`,
            color: isSystem ? 'text.secondary' : '#fff',
            fontSize: 10,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: isSystem ? 'none' : '0 1px 3px rgba(0,0,0,0.15)',
          }}
        >
          {isSystem ? 'S' : initials(note.author)}
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: isSystem ? 'text.secondary' : 'text.primary' }}>
          {note.author || 'Unknown'}
        </Typography>
        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
        <Tooltip title={new Date(note.ts).toLocaleString()}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatNoteTime(note.ts)}</Typography>
        </Tooltip>
        {isDraft && (
          <Chip
            size="small"
            label="Editing..."
            sx={{
              ml: 0.5,
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {!isSystem && (
          <Stack direction="row" spacing={0.25} sx={{ ml: 'auto' }}>
            {onEdit && (
              <Tooltip title="Edit note">
                <IconButton aria-label="Edit note" size="small" color="primary" onClick={() => onEdit(note)} sx={{ width: 24, height: 24 }}>
                  <EditOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete note">
                <IconButton aria-label="Delete note" size="small" color="error" onClick={() => onDelete(note)} sx={{ width: 24, height: 24 }}>
                  <DeleteOutlineIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
      <Box sx={{ fontStyle: isSystem ? 'italic' : 'normal', color: isSystem ? 'text.secondary' : 'text.primary' }}>
        <NoteText text={note.text} actionById={actionById} />
      </Box>
    </Box>
  );
};

export const InvestigationFeed = ({
  currentUserName,
  noteDraft,
  setNoteDraft,
  scheduleNoteSave,
  sealCurrentDraft,
  onEditNote,
  onDeleteNote,
  onApplyTemplate,
  notes = [],
  activeDraftNoteId = null,
  row = null,
  gmItems = [],
  cpqItems = [],
  appsheetData = null,
  varianceActions = [],
  runId = '',
}) => {
  const quality = noteQuality(noteDraft);
  const [templatePicker, setTemplatePicker] = React.useState(null);
  const [auditPreviewAnchor, setAuditPreviewAnchor] = React.useState(null);
  const {
    actionById,
    activeAuditAction,
    activeAuditLinkId,
    clearAuditLink,
    draftBody,
    setAuditLink,
    updateDraftBody,
  } = useInvestigationAuditLink({
    noteDraft,
    setNoteDraft,
    scheduleNoteSave,
    row,
    runId,
    varianceActions,
  });
  const closeTemplatePicker = () => setTemplatePicker(null);
  const draftBodyRef = React.useRef(draftBody);
  React.useEffect(() => {
    draftBodyRef.current = draftBody;
  }, [draftBody]);
  const draftFromInput = (value) => withAuditLinkToken(value || draftBodyRef.current, activeAuditLinkId);

  const handleDrop = (event) => {
    const raw = event.dataTransfer.getData('application/x-billing-audit-action');
    if (!raw) return;
    event.preventDefault();
    try {
      const payload = JSON.parse(raw);
      setAuditLink(payload.id);
    } catch {
      // Ignore unrelated drops.
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Investigation feed</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip size="small" color={quality.color} variant="outlined" label={quality.label} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Draft autosaves - blur or Enter commits</Typography>
          </Stack>
        </Stack>
        {onApplyTemplate ? (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {NOTE_TEMPLATES.map((template) => (
              <Chip
                key={template.id}
                size="small"
                variant="outlined"
                clickable
                label={template.label}
                onClick={(event) => {
                  setTemplatePicker({ anchorEl: event.currentTarget, templateId: template.id });
                }}
              />
            ))}
          </Stack>
        ) : null}
        {onApplyTemplate ? (
          <InvestigationTemplatePicker
            activeAuditAction={activeAuditAction || null}
            anchorEl={templatePicker?.anchorEl || null}
            appsheetData={appsheetData}
            cpqItems={cpqItems}
            gmItems={gmItems}
            onApplyTemplate={onApplyTemplate}
            onClose={closeTemplatePicker}
            templateId={templatePicker?.templateId || ''}
          />
        ) : null}
      {activeAuditLinkId ? (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>Attached audit</Typography>
          <AuditLinkChip action={activeAuditAction || { actionLabel: 'Audit link' }} onDelete={clearAuditLink} onClick={(event) => setAuditPreviewAnchor(event.currentTarget)} />
        </Stack>
      ) : null}
      <TextField
        fullWidth
        size="small"
        placeholder={activeAuditLinkId ? `Write as ${currentUserName}... audit link is attached` : `Write as ${currentUserName}... saves as you type`}
        value={draftBody}
        onChange={(event) => {
          draftBodyRef.current = event.target.value;
          updateDraftBody(event.target.value);
        }}
        onBlur={() => {
          const next = draftFromInput(event.currentTarget.value);
          if (!stripAuditLinkToken(next)) return;
          sealCurrentDraft(next);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            const next = draftFromInput(event.currentTarget.value);
            if (!stripAuditLinkToken(next)) return;
            sealCurrentDraft(next);
          }
        }}
        onDragOver={(event) => {
          if (event.dataTransfer.types.includes('application/x-billing-audit-action')) event.preventDefault();
        }}
        onDrop={handleDrop}
        multiline
        maxRows={4}
      />
      <AuditPreviewPopover anchorEl={auditPreviewAnchor} action={activeAuditAction || { actionLabel: 'Audit link', id: activeAuditLinkId }} onClose={() => setAuditPreviewAnchor(null)} />
      {notes.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          No notes yet. Start typing above; your name and timestamp are stamped automatically.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {[...notes]
            .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
            .map((note) => (
              <NoteCard
                key={note.id || note.ts}
                note={note}
                onEdit={onEditNote}
                onDelete={onDeleteNote}
                actionById={actionById}
                activeDraftNoteId={activeDraftNoteId}
              />
            ))}
        </Stack>
      )}
      </Stack>
    </Paper>
  );
};
